import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";
import {
  Briefcase,
  Plus,
  Search,
  FileText,
  Package,
  Truck,
  ChevronRight,
  Pencil,
  Trash2,
  X,
} from "lucide-react";

type JobCategory = "SI" | "BC" | "BB" | "BD";

type Job = {
  id: number;
  job_number: string;
  category: JobCategory;
  name: string;
  client_id: number | null;
  client_name: string | null;
  status: string;
  description: string | null;
  notes: string | null;
  start_date: string | null;
  created_at: string;
  wo_count?: number;
  bol_count?: number;
  total_tons?: number;
};

type Client = { id: number; name: string };

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  active: { label: "Active", bg: "bg-green-100 dark:bg-green-900/30", text: "text-green-700 dark:text-green-400" },
  completed: { label: "Completed", bg: "bg-blue-100 dark:bg-blue-900/30", text: "text-blue-700 dark:text-blue-400" },
  on_hold: { label: "On Hold", bg: "bg-yellow-100 dark:bg-yellow-900/30", text: "text-yellow-700 dark:text-yellow-400" },
  cancelled: { label: "Cancelled", bg: "bg-red-100 dark:bg-red-900/30", text: "text-red-700 dark:text-red-400" },
};

const CATEGORY_CONFIG: Record<JobCategory, { label: string; bg: string; text: string }> = {
  SI: { label: "Strategic Initiative", bg: "bg-purple-100 dark:bg-purple-900/30", text: "text-purple-700 dark:text-purple-400" },
  BC: { label: "B2C", bg: "bg-amber-100 dark:bg-amber-900/30", text: "text-amber-700 dark:text-amber-400" },
  BB: { label: "B2B", bg: "bg-sky-100 dark:bg-sky-900/30", text: "text-sky-700 dark:text-sky-400" },
  BD: { label: "Distributor", bg: "bg-emerald-100 dark:bg-emerald-900/30", text: "text-emerald-700 dark:text-emerald-400" },
};

export default function JobsList() {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [jobs, setJobs] = useState<Job[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<{
    name: string;
    category: JobCategory | "";
    client_id: string;
    status: string;
    description: string;
    notes: string;
  }>({
    name: "",
    category: "",
    client_id: "",
    status: "active",
    description: "",
    notes: "",
  });

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }),
    [token],
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [jobsRes, clientsRes] = await Promise.all([
        apiFetch("/api/ops/jobs", { headers }),
        apiFetch("/api/ops/clients", { headers }),
      ]);
      if (jobsRes.ok) {
        const d = await jobsRes.json();
        setJobs(d.jobs ?? d ?? []);
      }
      if (clientsRes.ok) {
        const d = await clientsRes.json();
        setClients(d.clients ?? d ?? []);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = useMemo(() => {
    let list = jobs;
    if (statusFilter !== "all") list = list.filter((j) => j.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (j) =>
          j.job_number.toLowerCase().includes(q) ||
          j.name.toLowerCase().includes(q) ||
          (j.client_name || "").toLowerCase().includes(q),
      );
    }
    return list;
  }, [jobs, statusFilter, search]);

  const openAdd = () => {
    setEditingJob(null);
    setForm({ name: "", category: "", client_id: "", status: "active", description: "", notes: "" });
    setShowModal(true);
  };

  const openEdit = (job: Job) => {
    setEditingJob(job);
    setForm({
      name: job.name,
      category: job.category,
      client_id: job.client_id ? String(job.client_id) : "",
      status: job.status,
      description: job.description || "",
      notes: job.notes || "",
    });
    setShowModal(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    if (!editingJob && !form.category) return;
    setSaving(true);
    try {
      const client = clients.find((c) => c.id === Number(form.client_id));
      const basePayload = {
        name: form.name.trim(),
        clientId: form.client_id ? Number(form.client_id) : null,
        clientName: client?.name || null,
        status: form.status,
        description: form.description.trim() || null,
        notes: form.notes.trim() || null,
      };
      if (editingJob) {
        // category is locked at creation — not sent on update
        await apiFetch(`/api/ops/jobs/${editingJob.id}`, { method: "PATCH", headers, body: JSON.stringify(basePayload) });
      } else {
        await apiFetch("/api/ops/jobs", {
          method: "POST",
          headers,
          body: JSON.stringify({ ...basePayload, category: form.category }),
        });
      }
      setShowModal(false);
      await loadData();
    } catch (err: any) {
      setError(err?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Delete job "${name}"?`)) return;
    try {
      await apiFetch(`/api/ops/jobs/${id}`, { method: "DELETE", headers });
      await loadData();
    } catch (err: any) {
      setError(err?.message || "Failed to delete");
    }
  };

  return (
    <div className="space-y-4 max-w-7xl">
      {/* Header + inline count */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 flex-wrap">
        <div className="flex items-baseline gap-4 flex-wrap">
          <h1 className="heading-display text-2xl text-foreground">Jobs</h1>
          {!loading && (
            <span className="text-sm text-muted-foreground">
              <strong className="text-foreground tabular-nums">{filtered.length}</strong> job{filtered.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        <button
          onClick={openAdd}
          className="inline-flex items-center gap-2 px-4 py-2 bg-forest-600 hover:bg-forest-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm"
        >
          <Plus size={16} strokeWidth={2.5} /> New Job
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search jobs..."
            className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-border rounded-lg"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm bg-background border border-border rounded-lg"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="on_hold">On Hold</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-sm text-red-700 dark:text-red-400">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {/* Job cards */}
      {loading ? (
        <div className="p-12 text-center text-muted-foreground">Loading jobs...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-16 text-center">
          <Briefcase size={40} className="mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-sm text-muted-foreground">{jobs.length === 0 ? "No jobs yet" : "No matching jobs"}</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filtered.map((job) => {
            const cfg = STATUS_CONFIG[job.status] || STATUS_CONFIG.active;
            return (
              <div
                key={job.id}
                onClick={() => navigate(`/app/ops/jobs/${job.id}`)}
                className="bg-card border border-border rounded-xl p-5 hover:border-forest-300 dark:hover:border-forest-700 cursor-pointer transition-all group"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-forest-100 dark:bg-forest-900/30 text-forest-700 dark:text-forest-400 flex items-center justify-center shrink-0">
                      <Briefcase size={18} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm font-bold text-foreground">{job.job_number}</span>
                        {job.category && CATEGORY_CONFIG[job.category] && (
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${CATEGORY_CONFIG[job.category].bg} ${CATEGORY_CONFIG[job.category].text}`}>
                            {CATEGORY_CONFIG[job.category].label}
                          </span>
                        )}
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg.bg} ${cfg.text}`}>
                          {cfg.label}
                        </span>
                      </div>
                      <h3 className="text-base font-semibold text-foreground mt-1">{job.name}</h3>
                      {job.client_name && (
                        <p className="text-sm text-muted-foreground mt-0.5">{job.client_name}</p>
                      )}
                      {job.description && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{job.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1" title="Work Orders">
                        <Package size={12} /> {job.wo_count ?? 0}
                      </span>
                      <span className="flex items-center gap-1" title="BOLs">
                        <FileText size={12} /> {job.bol_count ?? 0}
                      </span>
                      <span className="flex items-center gap-1 font-semibold text-foreground" title="Total Tons">
                        <Truck size={12} /> {(job.total_tons ?? 0).toFixed(1)}t
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); openEdit(job); }}
                        className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(job.id, job.name); }}
                        className="p-1.5 text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                      <ChevronRight size={16} className="text-muted-foreground/40 group-hover:text-forest-600 transition-colors" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowModal(false)} />
          <div className="relative w-full max-w-md bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800 shadow-2xl overflow-y-auto">
            <form onSubmit={handleSave} className="p-6 space-y-5">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  {editingJob ? "Edit Job" : "New Job"}
                </h2>
                <button type="button" onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400">
                  <X size={18} />
                </button>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Job Name *</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Pistachio Extract Delivery" required className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm" />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Category {!editingJob && <span className="text-red-500">*</span>}
                </label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value as JobCategory | "" })}
                  required={!editingJob}
                  disabled={!!editingJob}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm disabled:opacity-60"
                >
                  <option value="">Select a category...</option>
                  <option value="SI">SI — Strategic Initiative (10000s)</option>
                  <option value="BC">BC — Business to Consumer (20000s)</option>
                  <option value="BB">BB — Business to Business (30000s)</option>
                  <option value="BD">BD — Business to Distributor (40000s)</option>
                </select>
                {editingJob ? (
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">Category is locked after creation (job number is derived from it).</p>
                ) : (
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">The job number will be auto-generated in the format <span className="font-mono">XX-XXXXX-00</span>.</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Client</label>
                <select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm">
                  <option value="">No client</option>
                  {clients.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm">
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="on_hold">On Hold</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Description</label>
                <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Brief description of the job scope..." className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm resize-none" />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Notes</label>
                <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Internal notes..." className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm resize-none" />
              </div>

              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving || !form.name.trim() || (!editingJob && !form.category)} className="flex-1 px-4 py-2.5 bg-[#264027] hover:bg-[#1a2e1b] disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors">
                  {saving ? "Saving..." : editingJob ? "Save Changes" : "Create Job"}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg text-sm font-semibold transition-colors">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
